/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import * as Service from 'types/service';
import {
  REALTIME_POS_ACTION,
  RealtimePartyDetails,
  RealtimePosActionReturnType,
} from 'middleware/realtimePos';
import { dashboard } from 'types';
import { getPartyEmail, getPartyPhone, isPartyPurchase, isPartyWalkIn } from 'utils/partyUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  ConnectedExternalSystem,
  ConnectExternalSystemRequest,
  PosCheckSearchRequest,
  PosCheckAssociateRequest,
  PosCheckExcludeRequest,
  PosTableStatusUpdateRequest,
} = protos;

/**
 * Connect your POS to Tock
 */
export function connect(connectRequest: dashboard.IConnectExternalSystemRequest): ApiAction {
  const request = ConnectExternalSystemRequest.fromObject(connectRequest);
  return {
    TockApi: {
      postProto: '/pos/connect',
      request,
      toast: 'Connected',
      type: AppConstants.POS_CONNECT,
    },
  };
}

/**
 * Updates the settings of the POS
 */
export function update(updateRequest: dashboard.ConnectedExternalSystem): ApiAction {
  const request = ConnectedExternalSystem.fromObject(updateRequest);
  return {
    TockApi: {
      putProto: '/pos/connect',
      request,
      toast: `${_.upperFirst(_.lowerCase(updateRequest.system))} updated`,
      type: AppConstants.POS_CONNECT_UPDATE,
    },
  };
}

/**
 * Disconnect your POS from Tock
 *
 * @param {Object} request PosConnectRequest
 * @returns {Object} A Redux TockApi action.
 */
export function disconnect(connectRequest: any): ApiAction {
  const request = ConnectExternalSystemRequest.fromObject(connectRequest);
  return {
    TockApi: {
      postProto: '/pos/disconnect',
      toast: 'Disconnected',
      request,
      type: AppConstants.NULL,
    },
  };
}

/**
 * List POS systems connected to Tock
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listConnected(): ApiAction {
  return {
    TockApi: {
      getListProto: '/pos/connect',
      type: AppConstants.POS_LIST_CONNECTED,
    },
  };
}

/**
 * Search for POS Checks
 *
 * @param {Object} request PosCheckSearchRequest
 * @returns {Object} A Redux TockApi action.
 */
export function search(searchRequest: any): ApiAction {
  const request = PosCheckSearchRequest.fromObject(searchRequest);
  return {
    TockApi: {
      postProto: '/pos/check/search',
      request,
      type: AppConstants.POS_SEARCH_CHECKS,
      onError: {
        7003: '',
        defaultToOriginal: true,
      },
    },
  };
}

/**
 * Associate a POS Check with a Tock Visit
 *
 * @param {String} associateRequest PosCheckAssociateRequest
 * @returns {Object} A Redux TockApi action.
 */
export function associate(associateRequest: any): ApiAction {
  const request = PosCheckAssociateRequest.fromObject(associateRequest);
  return {
    TockApi: {
      postProto: '/pos/check/associate',
      request,
      toast: 'Check association updated',
      type: AppConstants.POS_ASSOCIATE_CHECK,
    },
  };
}

/**
 * Set the excluded state of a POS Check
 *
 * @param {String} excludeRequest PosCheckExcludeRequest
 * @returns {Object} A Redux TockApi action.
 */
export function exclude(excludeRequest: any): ApiAction {
  const request = PosCheckExcludeRequest.fromObject(excludeRequest);
  return {
    TockApi: {
      postProto: '/pos/check/exclude',
      request,
      toast: 'Check exclusion updated',
      type: AppConstants.POS_EXCLUDE_CHECK,
    },
  };
}

/**
 * Get visits that happened on the same day as the selected POS Check
 *
 * @param {String} serviceDate Date of Service of visit
 * @returns {Object} A Redux TockApi action.
 */
export function searchDayVisits(serviceDate: any): ApiAction {
  return {
    TockApi: {
      getListProto: `/visit/summary/search?serviceDate=${serviceDate}`,
      type: AppConstants.POS_SEARCH_DAY_VISITS,
    },
  };
}

/**
 * Informs the middleware of a real-time update of a table state.
 *
 * @param {RealtimePartyDetails} party The party to update
 * @param {string} newState The new state to attach to the party
 * @param {Service.ServiceTable[]} tables All of the tables to update to this given state.
 * @param {(string) => Promise<unknown>} errorCallback
 *   A function used to confirm action after any error in updating the real-tim pos.
 */
export function updateTableStates(
  party: RealtimePartyDetails,
  newState: string,
  tables: Service.ServiceTable[]
): RealtimePosActionReturnType {
  return {
    type: REALTIME_POS_ACTION,
    [REALTIME_POS_ACTION]: {
      newState,
      tables,
      party,
    },
  };
}

/**
 * Send an update that the party status has changed to a POS system, by issuing the update through Tock.
 */
export function updatePartyStatus(
  party: RealtimePartyDetails,
  newState: string,
  tables: Service.ServiceTable[]
): ApiAction {
  const tableNames = _.map(tables, 'name');
  const serverName = _.first(_.map(tables, 'serverName'));
  const serviceParty = party as Service.Party;
  const partyId = isPartyPurchase(serviceParty) ? serviceParty.purchase?.id : serviceParty.id;
  const request = PosTableStatusUpdateRequest.fromObject({
    tableName: tableNames,
    serverName,
    partyId,
    partyName: party.lastNameFirstName,
    partyState: newState,
    partySize: party.partySize,
    isWalkIn: isPartyWalkIn(party as Service.Party),
    email: getPartyEmail(party),
    phone: getPartyPhone(party),
  });
  return {
    TockApi: {
      putProto: '/pos/party/status',
      request,
      type: AppConstants.POS_UPDATE_PARTY_STATUS,
      onError: {
        3047: '', // A check already exists in the point of sale
        defaultToOriginal: true,
      },
    },
  };
}

/**
 * Resend the party status update with the override action provided.
 */
export function resendUpdatePartyStatus(request: dashboard.PosTableStatusUpdateRequest): ApiAction {
  return {
    TockApi: {
      putProto: '/pos/party/status',
      request,
      type: AppConstants.POS_RESEND_UPDATE_PARTY_STATUS,
    },
  };
}
